import React, { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import api from '../../api';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import './AllianceInfoPage.css';

const fmtDate = (d) => new Date(d).toLocaleDateString(getLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
const fmtNum = (n) => (n || 0).toLocaleString(getLocale()).replace(/ /g, ' ');

const Card = ({ icon, label, tone, children, className = '' }) => (
    <section className={`c2-card gen-card ${className}`}>
        <span className={`gen-card__label${tone ? ` gen-card__label--${tone}` : ''}`}><Icon name={icon} size={15} />{label}</span>
        {children}
    </section>
);

/* ── L'alliance ── */
const SeasonCard = ({ season }) => {
    const { t } = useLanguage();
    if (!season) {
        return (
            <Card icon="trophy" label={t('alliance.season', 'Saison')}>
                <h3 className="gen-card__title">{t('alliance.noSeason', 'Aucune saison en cours')}</h3>
                <p className="gen-card__text">{t('alliance.noSeasonHint', 'La prochaine saison sera annoncée par les administrateurs.')}</p>
            </Card>
        );
    }
    const start = new Date(season.startDate).getTime();
    const end = new Date(season.endDate).getTime();
    const progress = Math.min(100, Math.max(0, ((Date.now() - start) / (end - start)) * 100));
    return (
        <Card icon="trophy" label={t('alliance.currentSeason', 'Saison en cours')} tone="green">
            <h3 className="gen-card__title">{season.title}</h3>
            <p className="gen-card__text">{fmtDate(season.startDate)} → {fmtDate(season.endDate)}</p>
            <div className="gen-progress" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100} aria-label={t('alliance.seasonProgress', 'Avancement de la saison')}>
                <div className="gen-progress__bar" style={{ width: `${progress}%` }} />
            </div>
            <span className="gen-card__foot">{season.daysLeft <= 1 ? t('alliance.lastDay', 'Dernier jour') : t('alliance.daysLeft', '{n} jours restants', { n: season.daysLeft })}</span>
        </Card>
    );
};

const ReservationCard = ({ session, serverId }) => {
    const { t } = useLanguage();
    return (
        <Card icon="clipboard" label={t('alliance.reservationSheet', 'Fiche de réservation')} tone="purple">
            {session ? (
                <>
                    <h3 className="gen-card__title">{session.title}</h3>
                    <div className="gen-row">
                        <span className="gen-card__text">{t('alliance.swapPhase', 'Phase d\'échange')}</span>
                        {session.swapWindowOpen
                            ? <span className="c2-chip c2-chip--green"><Icon name="swap" size={13} />{t('alliance.inProgress', 'En cours')}</span>
                            : <span className="c2-chip"><Icon name="lock" size={13} />{t('alliance.finished', 'Terminée')}</span>}
                    </div>
                    <Link to={`/server/${serverId}/member/fiche`} className="c2-btn c2-btn--ghost c2-btn--sm gen-card__action">
                        {t('alliance.viewSheet', 'Voir la fiche')}
                        <Icon name="arrowRight" size={15} />
                    </Link>
                </>
            ) : (
                <>
                    <h3 className="gen-card__title">{t('alliance.noSheet', 'Aucune fiche en cours')}</h3>
                    <p className="gen-card__text">{t('alliance.noSheetHint', 'La fiche n\'a pas encore été publiée.')}</p>
                </>
            )}
        </Card>
    );
};

const AuctionCard = ({ auction, season, serverId }) => {
    const { t } = useLanguage();
    const top = auction?.topBids?.[0];
    const topZone = auction?.topZones?.[0];
    return (
        <Card icon="hammer" label={t('alliance.auctions', 'Enchères')} tone={auction?.open ? 'green' : undefined}>
            <h3 className="gen-card__title">{auction?.open ? t('alliance.auctionsOpen', 'Enchères ouvertes') : t('alliance.auctionsClosed', 'Enchères fermées')}</h3>
            {auction?.open ? (
                <p className="gen-card__text">{t('alliance.until', 'Jusqu\'au {date}', { date: new Date(auction.endsAt).toLocaleString(getLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) })}</p>
            ) : (
                <p className="gen-card__text">
                    {season
                        ? <>{t('alliance.opensAtEndOf', 'Ouverture à la fin de la saison')} <strong className="gen-strong">{season.title}</strong>.</>
                        : t('alliance.opensAtEndOfCurrent', 'Ouverture à la fin de la saison en cours.')}
                </p>
            )}
            <dl className="gen-facts">
                <div><dt>{t('alliance.topBid', 'Top enchère')}</dt><dd>{top ? `${top.zone} · ${fmtNum(top.amount)}` : '—'}</dd></div>
                <div><dt>{t('alliance.topZonePrev', 'Top zone (saison préc.)')}</dt><dd>{topZone ? `${topZone.zone} · ${fmtNum(topZone.amount)}` : '—'}</dd></div>
            </dl>
            {auction?.open && (
                <Link to={`/server/${serverId}/member/encheres`} className="c2-btn c2-btn--ghost c2-btn--sm gen-card__action">
                    {t('alliance.joinAuctions', 'Participer aux enchères')}
                    <Icon name="arrowRight" size={15} />
                </Link>
            )}
        </Card>
    );
};

/* ── Moi ── */
const PepitesCard = ({ data }) => {
    const { t } = useLanguage();
    return (
        <Card icon="gem" label={t('alliance.myPepites', 'Mes pépites')} tone="green">
            <div className="gen-split gen-split--single">
                <div className="gen-split__due">
                    <span className="gen-card__text">{t('alliance.owedToYou', 'On te doit')}</span>
                    <strong className="gen-big">{fmtNum(data?.payout)}</strong>
                </div>
            </div>
            <span className="gen-card__foot">
                {data?.lastDistribution
                    ? <>{t('alliance.lastDistribution', 'Dernière distribution le {date}', { date: fmtDate(data.lastDistribution.distributedAt) })}{data.lastDistribution.payout != null && <> · {t('alliance.received', '{n} reçues', { n: fmtNum(data.lastDistribution.payout) })}</>}</>
                    : t('alliance.paidNextDistribution', 'Versées à la prochaine distribution')}
            </span>
        </Card>
    );
};

const MyZonesCard = ({ reservation, serverId }) => {
    const { t } = useLanguage();
    const zones = reservation?.myZones || [];
    const session = reservation?.session;
    return (
        <Card icon="map" label={t('alliance.myZones', 'Mes zones cette saison')} tone="green">
            {zones.length === 0 ? (
                <>
                    <p className="gen-card__text">
                        {session ? t('alliance.noZoneOnSheet', 'Aucune zone ne t\'est attribuée sur la fiche en cours.') : t('alliance.noZoneYet', 'Aucune zone réservée pour le moment.')}
                    </p>
                    {session && (
                        <Link to={`/server/${serverId}/member/preferences`} className="c2-btn c2-btn--ghost c2-btn--sm gen-card__action">
                            {t('alliance.checkPreferences', 'Vérifier mes préférences')}
                        </Link>
                    )}
                </>
            ) : (
                <ul className="gen-zones">
                    {zones.map((z, i) => (
                        <li key={`${z.zone}-${i}`}>
                            <span className="gen-zones__name">
                                <strong>{z.zone}</strong>
                                {z.donjon && <span>{z.donjon}</span>}
                            </span>
                            <span className={`c2-chip${z.source === 'enchere' ? ' c2-chip--purple' : ''}`}>
                                {z.source === 'enchere' ? t('alliance.sourceAuction', 'Enchère') : session?.frozen ? t('alliance.sheetFrozen', 'Fiche figée') : t('alliance.provisional', 'Provisoire')}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
};

const MyAuctionCard = ({ myAuction, auction, serverId }) => {
    const { t } = useLanguage();
    const lots = myAuction?.lots || [];
    const running = !!myAuction?.running;
    const leading = lots.filter(l => l.leader);
    const outbid = lots.filter(l => !l.leader);
    return (
        <Card icon="hammer" label={t('alliance.myAuctions', 'Mes enchères')} tone="purple">
            {lots.length === 0 ? (
                <>
                    <p className="gen-card__text">
                        {auction?.open ? t('alliance.noBidYet', 'Tu n\'as encore misé sur aucune zone.') : t('alliance.noLastAuction', 'Tu n\'as pas participé à la dernière enchère.')}
                    </p>
                    {auction?.open && (
                        <Link to={`/server/${serverId}/member/encheres`} className="c2-btn c2-btn--ghost c2-btn--sm gen-card__action">
                            {t('alliance.joinAuctions', 'Participer aux enchères')}
                            <Icon name="arrowRight" size={15} />
                        </Link>
                    )}
                </>
            ) : (
                <>
                    <div className="gen-row">
                        {running ? (
                            <>
                                <span className="c2-chip c2-chip--green">{t('alliance.leading', 'En tête')} · {leading.length}</span>
                                {outbid.length > 0 && <span className="c2-chip gen-chip--warn">{t('alliance.outbid', 'Dépassé')} · {outbid.length}</span>}
                            </>
                        ) : (
                            <span className="c2-chip c2-chip--purple">
                                {leading.length > 1
                                    ? t('alliance.zonesWon', '{n} zones remportées', { n: leading.length })
                                    : t('alliance.zoneWon', '{n} zone remportée', { n: leading.length })}
                            </span>
                        )}
                    </div>
                    <ul className="gen-zones">
                        {(running ? lots : leading).map(l => (
                            <li key={l.zone}>
                                <span className="gen-zones__name">
                                    <strong>{l.zone}</strong>
                                    <span>{l.leader
                                        ? t('alliance.yourBid', 'Ta mise : {n}', { n: fmtNum(l.amount) })
                                        : t('alliance.bestBidVsYours', 'Meilleure mise : {n} · la tienne : {mine}', { n: fmtNum(l.amount), mine: fmtNum(l.myBest) })}</span>
                                </span>
                                {l.leader
                                    ? <span className="c2-chip c2-chip--green">{l.ended ? t('alliance.won', 'Remportée') : t('alliance.topBidChip', 'Top mise')}</span>
                                    : <span className="c2-chip gen-chip--warn">{l.ended ? t('alliance.lost', 'Perdue') : t('alliance.outbid', 'Dépassé')}</span>}
                            </li>
                        ))}
                        {!running && leading.length === 0 && <li><span className="gen-card__text">{t('alliance.noZoneWon', 'Aucune zone remportée.')}</span></li>}
                    </ul>
                    {running && (
                        <Link to={`/server/${serverId}/member/encheres`} className="c2-btn c2-btn--ghost c2-btn--sm">
                            {outbid.length > 0 ? t('alliance.rebid', 'Remiser') : t('alliance.viewAuctions', 'Voir les enchères')}
                            <Icon name="arrowRight" size={15} />
                        </Link>
                    )}
                </>
            )}
        </Card>
    );
};

const AllianceInfoPage = () => {
    const { t } = useLanguage();
    const { serverId } = useParams();
    const { server } = useOutletContext() || {};
    const [data, setData] = useState(null);
    const [pepites, setPepites] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setData(null);
        setError(false);
        api.get(`/alliance/${serverId}`)
            .then(res => { if (!cancelled) setData(res.data); })
            .catch(() => { if (!cancelled) setError(true); });
        api.get(`/pepites/${serverId}/me`)
            .then(res => { if (!cancelled) setPepites(res.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [serverId]);

    const reservationEnabled = data?.reservation?.enabled;

    return (
        <div className="c2-scope c2-admin-page gen-page">
            <PageHeader icon="home" title={t('member.nav.general', 'Informations générales')} subtitle={server?.name} />

            {error && <p className="gen-error" role="alert">{t('alliance.loadError', 'Impossible de charger les informations.')}</p>}

            {!data && !error ? (
                <div className="c2-loading">
                    <div className="c2-spinner" />
                    <p>{t('common.loading', 'Chargement...')}</p>
                </div>
            ) : data && (
                <>
                    <div className="gen-category">
                        <h2 className="gen-category__title"><span className="c2-diamond" />{t('alliance.theAlliance', 'L\'alliance')}</h2>
                        <div className="gen-grid gen-grid--alliance">
                            <SeasonCard season={data.currentSeason} />
                            {reservationEnabled && <ReservationCard session={data.reservation.session} serverId={serverId} />}
                            <AuctionCard auction={data.auction} season={data.currentSeason} serverId={serverId} />
                        </div>
                    </div>

                    <div className="gen-category">
                        <h2 className="gen-category__title"><span className="c2-diamond c2-diamond--purple" />{t('alliance.me', 'Moi')}</h2>
                        <div className="gen-grid gen-grid--perso">
                            <PepitesCard data={pepites} />
                            {reservationEnabled && <MyZonesCard reservation={data.reservation} serverId={serverId} />}
                            {reservationEnabled && <MyAuctionCard myAuction={data.myAuction} auction={data.auction} serverId={serverId} />}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AllianceInfoPage;
